import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  MdAssignment, MdPeople, MdAdd, MdDelete, MdPersonAdd,
  MdCheckCircleOutline, MdPendingActions, MdBarChart,
  MdFilterList, MdOutlineTask, MdOutlineGroup
} from "react-icons/md";
import { FaTasks } from "react-icons/fa";
import "./AdminDashboard.css";

const API = "https://daily-report.spectratally.net/api";

const priorityCls = p => p==="High"?"badge-high":p==="Low"?"badge-low":"badge-med";

function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div className={`toast-msg ${toast.type}`}>
      {toast.type==="success" ? <MdCheckCircleOutline /> : <MdPendingActions />}
      {toast.msg}
    </div>
  );
}

export default function AdminDashboard({ user }) {
  const [users, setUsers]   = useState([]);
  const [tasks, setTasks]   = useState([]);
  const [toast, setToast]   = useState(null);
  const [filterUser, setFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("tasks");
  const [taskForm, setTaskForm] = useState({ title:"", description:"", assignedTo:"", priority:"Medium", dueDate:"" });
  const [newUser, setNewUser]   = useState({ name:"", username:"", password:"", department:"" });

  const showToast = (msg, type="success") => { setToast({msg,type}); setTimeout(()=>setToast(null),3000); };

  const loadUsers = useCallback(async () => {
    try { const r = await axios.get(`${API}/auth/users`); setUsers(r.data.data); }
    catch { showToast("Failed to load users","error"); }
  }, []);

  const loadTasks = useCallback(async () => {
    try { const r = await axios.get(`${API}/tasks`); setTasks(r.data.data); }
    catch { showToast("Failed to load tasks","error"); }
  }, []);

  useEffect(() => { loadUsers(); loadTasks(); }, [loadUsers, loadTasks]);

  const assignTask = async () => {
    if (!taskForm.title || !taskForm.assignedTo) return showToast("Title and assignee required","error");
    const assignedUser = users.find(u=>u.id===taskForm.assignedTo);
    try {
      await axios.post(`${API}/tasks`, { ...taskForm, assignedToName: assignedUser?.name||"" });
      setTaskForm({ title:"", description:"", assignedTo:"", priority:"Medium", dueDate:"" });
      loadTasks(); showToast("Task assigned successfully");
    } catch { showToast("Failed to assign task","error"); }
  };

  const deleteTask = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    await axios.delete(`${API}/tasks/${id}`); loadTasks(); showToast("Task deleted");
  };

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/tasks/${id}`, { status }); loadTasks();
  };

  const addUser = async () => {
    if (!newUser.name||!newUser.username||!newUser.password) return showToast("All fields required","error");
    try {
      await axios.post(`${API}/auth/users`, { ...newUser, role:"user" });
      setNewUser({ name:"", username:"", password:"", department:"" });
      loadUsers(); showToast("User added successfully");
    } catch(err) { showToast(err.response?.data?.message||"Failed","error"); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Remove this user?")) return;
    try { await axios.delete(`${API}/auth/users/${id}`); loadUsers(); showToast("User removed"); }
    catch(err) { showToast(err.response?.data?.message||"Cannot delete","error"); }
  };

  const nonAdmin = users.filter(u=>u.role!=="admin");
  const filteredTasks = filterUser==="all" ? tasks : tasks.filter(t=>t.assignedTo===filterUser);
  const pending = tasks.filter(t=>t.status==="Pending").length;
  const inProg  = tasks.filter(t=>t.status==="In Progress").length;
  const done    = tasks.filter(t=>t.status==="Done").length;

  return (
    <div className="admin-wrap">
      <div className="stats-grid">
        {[
          { label:"Team Members", val:nonAdmin.length, icon:<MdOutlineGroup />,      color:"#3b82f6" },
          { label:"Total Tasks",  val:tasks.length,    icon:<MdOutlineTask />,        color:"#8b5cf6" },
          { label:"Pending",      val:pending,         icon:<MdPendingActions />,     color:"#f59e0b" },
          { label:"In Progress",  val:inProg,          icon:<MdBarChart />,           color:"#f97316" },
          { label:"Completed",    val:done,            icon:<MdCheckCircleOutline />, color:"#22c55e" },
        ].map(s => (
          <div className="stat-card" key={s.label}>
            <div className="stat-icon" style={{background:`${s.color}18`,color:s.color}}>{s.icon}</div>
            <div className="stat-body">
              <div className="stat-val">{s.val}</div>
              <div className="stat-label">{s.label}</div>
            </div>
            <div className="stat-bar" style={{background:`${s.color}30`}}>
              <div className="stat-bar-fill" style={{width:`${Math.min((s.val/Math.max(tasks.length||nonAdmin.length,1))*100,100)}%`,background:s.color}} />
            </div>
          </div>
        ))}
      </div>

      <div className="admin-tabs">
        {[["tasks","Tasks"],["users","Users"]].map(([id,label])=>(
          <button key={id} className={`atab ${activeTab===id?"active":""}`} onClick={()=>setActiveTab(id)}>
            {id==="tasks"?<FaTasks />:<MdPeople />} {label}
            <span className="atab-count">{id==="tasks"?tasks.length:nonAdmin.length}</span>
          </button>
        ))}
      </div>

      {activeTab==="tasks" && (
        <>
          <div className="panel">
            <div className="panel-hd"><MdAssignment /> Assign New Task</div>
            <div className="panel-bd">
              <div className="task-form-grid">
                <div className="tf col-full">
                  <label>Task Title <span className="req">*</span></label>
                  <input placeholder="e.g. Prepare daily report for today"
                    value={taskForm.title} onChange={e=>setTaskForm({...taskForm,title:e.target.value})} />
                </div>
                <div className="tf col-full">
                  <label>Description</label>
                  <textarea placeholder="Task details (optional)..."
                    value={taskForm.description} onChange={e=>setTaskForm({...taskForm,description:e.target.value})} />
                </div>
                <div className="tf">
                  <label>Assign To <span className="req">*</span></label>
                  <select value={taskForm.assignedTo} onChange={e=>setTaskForm({...taskForm,assignedTo:e.target.value})}>
                    <option value="">— Select Employee —</option>
                    {nonAdmin.map(u=><option key={u.id} value={u.id}>{u.name} ({u.department||"N/A"})</option>)}
                  </select>
                </div>
                <div className="tf">
                  <label>Priority</label>
                  <select value={taskForm.priority} onChange={e=>setTaskForm({...taskForm,priority:e.target.value})}>
                    <option>Low</option><option>Medium</option><option>High</option>
                  </select>
                </div>
                <div className="tf">
                  <label>Due Date</label>
                  <input type="date" value={taskForm.dueDate} onChange={e=>setTaskForm({...taskForm,dueDate:e.target.value})} />
                </div>
                <div className="tf btn-tf">
                  <button className="btn-primary" onClick={assignTask}><MdAdd /> Assign Task</button>
                </div>
              </div>
            </div>
          </div>

          <div className="panel">
            <div className="panel-hd">
              <span><FaTasks /> All Tasks ({filteredTasks.length})</span>
              <div className="filter-wrap">
                <MdFilterList />
                <select className="filter-sel" value={filterUser} onChange={e=>setFilter(e.target.value)}>
                  <option value="all">All Employees</option>
                  {nonAdmin.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            </div>
            <div className="panel-bd p0">
              {filteredTasks.length===0 ? (
                <div className="empty-state"><FaTasks /><p>No tasks found</p></div>
              ) : (
                <div className="table-scroll">
                  <table className="data-table">
                    <thead><tr>
                      <th>Task</th><th>Assigned To</th><th>Priority</th>
                      <th>Due Date</th><th>Status</th><th>Action</th>
                    </tr></thead>
                    <tbody>
                      {filteredTasks.map(t=>(
                        <tr key={t.id}>
                          <td>
                            <div className="task-title-cell">{t.title}</div>
                            {t.description && <div className="task-desc-cell">{t.description}</div>}
                          </td>
                          <td><div className="assignee-chip">{(t.assignedToName||"—").charAt(0)}<span>{t.assignedToName||"—"}</span></div></td>
                          <td><span className={`badge ${priorityCls(t.priority)}`}>{t.priority}</span></td>
                          <td className="date-cell">{t.dueDate||"—"}</td>
                          <td>
                            <select className={`status-sel status-${t.status.replace(" ","-").toLowerCase()}`}
                              value={t.status} onChange={e=>updateStatus(t.id,e.target.value)}>
                              <option>Pending</option>
                              <option>In Progress</option>
                              <option>Done</option>
                            </select>
                          </td>
                          <td>
                            <button className="icon-btn-del" onClick={()=>deleteTask(t.id)} title="Delete"><MdDelete /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {activeTab==="users" && (
        <div className="panel">
          <div className="panel-hd"><MdPeople /> Manage Users</div>
          <div className="panel-bd">
            <div className="add-user-bar">
              <div className="tf"><label>Full Name <span className="req">*</span></label>
                <input placeholder="Suresh Verma" value={newUser.name} onChange={e=>setNewUser({...newUser,name:e.target.value})} /></div>
              <div className="tf"><label>Username <span className="req">*</span></label>
                <input placeholder="suresh" value={newUser.username} onChange={e=>setNewUser({...newUser,username:e.target.value})} /></div>
              <div className="tf"><label>Password <span className="req">*</span></label>
                <input type="password" placeholder="password" value={newUser.password} onChange={e=>setNewUser({...newUser,password:e.target.value})} /></div>
              <div className="tf"><label>Department</label>
                <input placeholder="CS / Ops" value={newUser.department} onChange={e=>setNewUser({...newUser,department:e.target.value})} /></div>
              <div className="tf btn-tf">
                <button className="btn-primary" onClick={addUser}><MdPersonAdd /> Add User</button>
              </div>
            </div>
            <table className="data-table">
              <thead><tr>
                <th>Employee</th><th>Username</th><th>Department</th>
                <th>Role</th><th>Tasks</th><th>Action</th>
              </tr></thead>
              <tbody>
                {users.map(u=>(
                  <tr key={u.id}>
                    <td><div className="user-cell">
                      <div className="u-avatar">{u.name.charAt(0)}</div>
                      <span>{u.name}</span>
                    </div></td>
                    <td><code className="code-text">{u.username}</code></td>
                    <td>{u.department||"—"}</td>
                    <td><span className={`badge ${u.role==="admin"?"badge-admin":"badge-user"}`}>{u.role}</span></td>
                    <td><span className="count-pill">{tasks.filter(t=>t.assignedTo===u.id).length}</span></td>
                    <td>{u.role!=="admin"&&(
                      <button className="icon-btn-del" onClick={()=>deleteUser(u.id)} title="Remove user"><MdDelete /></button>
                    )}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <Toast toast={toast} />
    </div>
  );
}
